import { getSignalRUrl, isSignalRDisabled } from "@/configs/environment.config";
import type { HubConnection } from "@microsoft/signalr";
import { createHeartbeatManager } from "./heartbeat";
import { SignalRClient } from "./signalr-client";
import { useConnectionStore } from "./connection-store";

export type RealtimeEventType =
  | "connection.state.changed"
  | "device.online"
  | "device.offline"
  | "device.updated"
  | "camera.updated"
  | "gps.updated"
  | "camera.status.changed"
  | "vehicle.updated"
  | "playback.status";

export interface RealtimeEventBase {
  id: string;
  type: RealtimeEventType;
  source: "signalr" | "system";
  timestamp: number;
  payload?: Record<string, unknown>;
}

export interface DeviceOnlineEvent extends RealtimeEventBase {
  type: "device.online";
  payload: {
    deviceId: number;
    status: "online";
    [key: string]: unknown;
  };
}

export interface DeviceOfflineEvent extends RealtimeEventBase {
  type: "device.offline";
  payload: {
    deviceId: number;
    status: "offline";
    [key: string]: unknown;
  };
}

export interface DeviceUpdatedEvent extends RealtimeEventBase {
  type: "device.updated";
  payload: {
    deviceId: number;
    online?: boolean;
    status?: string;
    lastSeenAt?: string;
    signal?: number;
    speed?: number;
    heading?: number;
    latitude?: number;
    longitude?: number;
    [key: string]: unknown;
  };
}

export interface GpsUpdatedEvent extends RealtimeEventBase {
  type: "gps.updated";
  payload: {
    deviceId: number;
    latitude: number;
    longitude: number;
    speed?: number;
    heading?: number;
    signal?: number;
    [key: string]: unknown;
  };
}

export interface CameraUpdatedEvent extends RealtimeEventBase {
  type: "camera.updated";
  payload: {
    cameraId: number;
    deviceId?: number;
    status?: string;
    codec?: string;
    isStreaming?: boolean;
    [key: string]: unknown;
  };
}

export interface CameraStatusChangedEvent extends RealtimeEventBase {
  type: "camera.status.changed";
  payload: {
    cameraId: string;
    status: string;
    [key: string]: unknown;
  };
}

export interface VehicleUpdatedEvent extends RealtimeEventBase {
  type: "vehicle.updated";
  payload: {
    deviceId: number;
    speed?: number;
    status?: string;
    [key: string]: unknown;
  };
}

export interface PlaybackStatusEvent extends RealtimeEventBase {
  type: "playback.status";
  payload: {
    sessionId?: string;
    status: string;
    [key: string]: unknown;
  };
}

export type RealtimeEvent =
  | DeviceOnlineEvent
  | DeviceOfflineEvent
  | DeviceUpdatedEvent
  | GpsUpdatedEvent
  | CameraUpdatedEvent
  | CameraStatusChangedEvent
  | VehicleUpdatedEvent
  | PlaybackStatusEvent;

export type EventHandler<T extends RealtimeEvent = RealtimeEvent> = (
  event: T,
) => void;

export class EventDispatcher {
  private handlers = new Map<string, Set<EventHandler>>();

  subscribe<T extends RealtimeEvent>(eventType: RealtimeEventType, handler: EventHandler<T>): () => void {
    const set = this.handlers.get(eventType) ?? new Set();
    set.add(handler as EventHandler);
    this.handlers.set(eventType, set);

    return () => {
      const current = this.handlers.get(eventType);
      if (!current) {
        return;
      }

      current.delete(handler as EventHandler);
      if (current.size === 0) {
        this.handlers.delete(eventType);
      }
    };
  }

  emit<T extends RealtimeEvent>(event: T): void {
    console.debug("[EventDispatcher] emit", event.type, event.payload);
    
    const handlers = this.handlers.get(event.type) ?? new Set();
    handlers.forEach((handler) => {
      handler(event as RealtimeEvent);
    });
  }
}

export class ConnectionManager {
  private static instance: ConnectionManager | null = null;

  private readonly dispatcher = new EventDispatcher();
  private readonly heartbeat = createHeartbeatManager((sample) => {
    const store = useConnectionStore.getState();
    store.setLatency(sample.latency);
    store.setServerTime(sample.serverTime);
  });

  private client: SignalRClient | null = null;
  private started = false;
  private connectPromise: Promise<void> | null = null;

  private constructor() {}

  static getInstance(): ConnectionManager {
    if (!ConnectionManager.instance) {
      ConnectionManager.instance = new ConnectionManager();
    }

    return ConnectionManager.instance;
  }

  getEventDispatcher(): EventDispatcher {
    return this.dispatcher;
  }

  subscribe<T extends RealtimeEvent>(eventType: RealtimeEventType, handler: EventHandler<T>): () => void {
    //console.log("[ConnectionManager] subscribe", eventType);
    return this.dispatcher.subscribe(eventType, handler);
  }

  emit<T extends RealtimeEvent>(event: T): void {
    //console.debug("[ConnectionManager] emit", event.type, event.payload);
    this.dispatcher.emit(event);
  }

  async start(): Promise<void> {
    if (isSignalRDisabled()) {
      const store = useConnectionStore.getState();
      store.setConnectionState("disconnected");
      store.setSignalrConnected(false);
      store.setApiConnected(true);
      return;
    }

    if (this.started) {
      return;
    }

    if (this.connectPromise) {
      return this.connectPromise;
    }

    this.connectPromise = this.initialize();
    try {
      await this.connectPromise;
    } finally {
      this.connectPromise = null;
    }
  }

  async stop(): Promise<void> {
    this.started = false;
    this.heartbeat.stop();
    if (this.client) {
      await this.client.stop();
      this.client = null;
    }
    const store = useConnectionStore.getState();
    store.setConnectionState("disconnected");
    store.setSignalrConnected(false);
    store.setApiConnected(false);
    store.markDisconnected();
  }

  async reconnect(): Promise<void> {
    const store = useConnectionStore.getState();
    store.incrementReconnectCount();
    store.markReconnecting();
    if (this.client) {
      await this.client.reconnect();
      this.started = true;
      this.heartbeat.start();
      return;
    }

    await this.start();
  }

  async initialize(): Promise<void> {
    const store = useConnectionStore.getState();
    store.setApiConnected(true);
    store.setConnectionState("connecting");

    this.client = new SignalRClient({
      url: getSignalRUrl(),
      autoReconnect: true,
      reconnectDelay: 2500,
    });

    try {
      await this.client.start();
      this.started = true;
      store.setConnectionState("connected");
      store.setSignalrConnected(true);
      store.markConnected(Date.now());
      this.heartbeat.start();
    } catch (error) {
      this.started = false;
      store.setConnectionState("disconnected");
      store.setSignalrConnected(false);
      store.markDisconnected();
      console.error("Realtime connection initialization failed", error);
    }
  }

  isStarted(): boolean {
    return this.started;
  }

  getRawConnection(): HubConnection | null {
    return this.client?.getConnection() ?? null;
  }
}

export const realtimeConnectionManager = ConnectionManager.getInstance();
export const connectionManager = realtimeConnectionManager;

export function createConnectionManager(): ConnectionManager {
  return ConnectionManager.getInstance();
}
